import React, { useState, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { DashboardCard } from '../components/DashboardCard'

// Mock data for invoice amounts - in real app this would come from API
const mockInvoiceAmounts = {
  'INV-2568-09-001': 1600,
  'EXP-001': 55,
  'EXP-002': 50,
  'EXP-003': 50
}

export const PaymentSlipUploadPage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Get amount from params or mock data
  const amount = (location.state as any)?.amount || (invoiceId ? mockInvoiceAmounts[invoiceId as keyof typeof mockInvoiceAmounts] : 0)
  const returnPath = (location.state as any)?.returnPath || -1

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!invoiceId) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <PageHeader 
          title="ไม่พบใบแจ้งหนี้"
          subtitle="ใบแจ้งหนี้ที่ท่านกำลังมองหาไม่พบในระบบ"
        />
        <div className="px-4 py-6">
          <DashboardCard title="ไม่พบข้อมูล">
            <div className="text-center py-8">
              <span className="text-4xl mb-4 block">❌</span>
              <p className="text-gray-600 mb-4">ไม่พบใบแจ้งหนี้ที่ท่านกำลังมองหา</p>
              <button 
                onClick={() => navigate(-1)}
                className="btn-primary"
              >
                ← กลับ
              </button>
            </div>
          </DashboardCard>
        </div>
        <TabNavigation />
      </div>
    )
  }

  const formatThaiBaht = (amount: number) => {
    return `฿${new Intl.NumberFormat('th-TH').format(amount)}`
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('กรุณาเลือกไฟล์รูปภาพเท่านั้น (JPG, PNG)')
      return
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('ไฟล์ใหญ่เกินไป กรุณาเลือกไฟล์ที่เล็กกว่า 5MB')
      return
    }

    setSelectedFile(file)
    setUploadError(null)

    // Create preview URL
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
  }

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('capture', 'camera')
      fileInputRef.current.click()
    }
  }

  const handleGallerySelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.removeAttribute('capture')
      fileInputRef.current.click()
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('กรุณาเลือกไฟล์ก่อนอัพโหลด')
      return
    }

    setIsUploading(true)
    setUploadError(null)

    try {
      // Mock upload process
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Mock upload success
      const fileInfo = {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        uploadDate: new Date().toISOString()
      }

      console.log('Upload successful for invoice:', invoiceId, fileInfo)
      
      // Navigate to success page instead of showing alert
      navigate('/payment-slip/success', {
        state: {
          invoiceId,
          amount,
          fileName: selectedFile.name,
          returnPath: typeof returnPath === 'string' ? returnPath : '/payment'
        },
        replace: true
      })
      
    } catch (error) {
      setUploadError('เกิดข้อผิดพลาดในการอัพโหลด กรุณาลองใหม่อีกครั้ง')
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancel = () => {
    if (typeof returnPath === 'string') {
      navigate(returnPath)
    } else {
      navigate(-1)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader 
        title="📸 อัพโหลดสลิปการโอน"
        subtitle={`ใบแจ้งหนี้: ${invoiceId} • ${formatThaiBaht(amount)}`}
        actions={
          <button 
            onClick={handleCancel}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ← กลับ
          </button>
        }
      />
      
      <div className="px-4 py-6 space-y-6">
        {/* File Upload Section */}
        <DashboardCard title="📤 เลือกไฟล์สลิป">
          <div className="space-y-4">
            {/* Upload Buttons */}
            {!selectedFile && (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleCameraCapture}
                  className="flex flex-col items-center p-6 bg-green-50 hover:bg-green-100 rounded-lg border-2 border-dashed border-green-300 transition-colors"
                >
                  <span className="text-3xl mb-2">📷</span>
                  <span className="text-sm font-medium text-green-800">ถ่ายรูป</span>
                </button>
                
                <button
                  onClick={handleGallerySelect}
                  className="flex flex-col items-center p-6 bg-blue-50 hover:bg-blue-100 rounded-lg border-2 border-dashed border-blue-300 transition-colors"
                >
                  <span className="text-3xl mb-2">🖼️</span>
                  <span className="text-sm font-medium text-blue-800">เลือกจากแกลเลอรี่</span>
                </button>
              </div>
            )}

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Preview Section */}
            {previewUrl && (
              <div className="space-y-3">
                <div className="relative">
                  <img
                    src={previewUrl}
                    alt="สลิปการโอน"
                    className="w-full max-w-sm mx-auto rounded-lg border"
                  />
                  <button
                    onClick={handleRemoveFile}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    ✕
                  </button>
                </div>
                
                {selectedFile && (
                  <div className="text-center text-sm text-gray-600">
                    <p>{selectedFile.name}</p>
                    <p>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                )}
              </div>
            )}

            {/* Error Message */}
            {uploadError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{uploadError}</p>
              </div>
            )}
          </div>
        </DashboardCard>

        {/* Payment Summary */}
        <DashboardCard title="💰 สรุปการชำระเงิน" className="bg-gray-50">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">เลขที่แจ้งค่าใช้จ่าย:</span>
              <span className="font-medium">{invoiceId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">จำนวนเงิน:</span>
              <span className="font-semibold text-lg text-blue-600">{formatThaiBaht(amount)}</span>
            </div>
          </div>
        </DashboardCard>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={handleCancel}
            className="btn-secondary flex-1"
            disabled={isUploading}
          >
            ยกเลิก
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            className={clsx(
              'btn-primary flex-1',
              isUploading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isUploading ? '⏳ กำลังส่ง...' : '📤 ส่งสลิป'}
          </button>
        </div>
      </div>
      
      <TabNavigation />
    </div>
  )
}